import { useTheme, useToast } from '@quadminds/mindkit';
import { motion } from 'framer-motion';
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useRateConversation from '../../../../../hooks/useRateConversation';
import { useChatContext } from '../../../../../providers/Chat/Chat.provider';
import { ConversationType } from '../../../../../types/Conversation/Conversation';
import AnimatedDots from '../../../../common/AnimatedDots/AnimatedDots';
import ResponseAttachment from '../../../../common/ResponseAttachment/ResponseAttachment';
import Typewriter from '../../Chat/ChatTexter';
import { Attachment } from '../../Chat/types';
import { ThinkingLoader } from '../../ThinkingLoader';
import FeedbackBox, { FeedbackConversation } from '../FeedbackBox/FeedbackBox';
import MessageActions from '../MessageActions';
import './markdown.css';

type RatingValue = 'up' | 'down';

const RATE_MAP: Record<RatingValue, 'positive' | 'negative'> = {
    up: 'positive',
    down: 'negative',
};

const normalizeMarkdownText = (rawText: string): string => {
    let normalized = rawText.replaceAll(/\\n/g, '\n');

    normalized = normalized.replaceAll(/(\|[ \t]*)\n(?!\|)/g, '$1\n\n');

    // Ensure blank line before a table row when preceded by non-table content.
    // GFM requires an empty line before a table; the next regex skips \n before |.
    normalized = normalized.replaceAll(/([^\n|])\n(\|)/g, '$1\n\n$2');

    normalized = normalized.replaceAll(/(?<!\n)\n(?![\n|])/g, '\n\n');

    normalized = normalized.replaceAll(/\n{3,}/g, '\n\n');

    return normalized;
};

/*
 * B-4 · Detección de celda numérica.
 *
 * Poppins tiene cifras de ancho proporcional (el `1` mide 5,12 px y el `6` mide 10,16 px,
 * medido con la fuente cargada), así que las comas de una columna de temperaturas no
 * alinean. Las celdas que son un número puro se marcan para que markdown.css les aplique
 * Roboto, que sí tiene los diez dígitos del mismo ancho.
 *
 * Deliberadamente conservador: solo matchea un número con separadores y, opcionalmente,
 * una unidad corta (°C, %, km, kg, hs, min). Un código como `ANDES018` o `BKTR41` no
 * matchea, que es lo que queremos — esos van en Poppins como el resto del texto.
 *
 * Separado en número + unidad (en vez de una sola regex con la unidad como alternancia)
 * porque Sonar marcaba la regex única con complejidad 25 sobre 20 permitidos. Mismo
 * comportamiento, verificado con una batería de casos contra la regex original antes del
 * cambio (incluye los valores reales de logisticsStream.handler.ts).
 */
// Prefijo determinístico, no una regex con dos cuantificadores adyacentes sobre
// clases de caracteres superpuestas (`[\d.,]+\s*\S*`): Sonar marcaba esa forma
// como vulnerable a ReDoS (backtracking súper-lineal) porque [\d.,] y \S se solapan.
// Esta regex matchea el prefijo numérico más largo posible sin ambigüedad — cada
// repetición de los grupos opcionales exige primero un separador literal — y lo que
// sobra después se compara contra el Set de unidades con slice(), sin regex.
const NUMERO_PREFIJO = /^[-+]?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?/;
const UNIDADES_CORTAS = new Set(['°c', '°f', '%', 'km', 'kg', 'h', 'hs', 'min', 'm', 's']);

const textoDe = (nodo: React.ReactNode): string => {
    if (nodo === null || nodo === undefined || typeof nodo === 'boolean') return '';
    if (typeof nodo === 'string' || typeof nodo === 'number') return String(nodo);
    if (Array.isArray(nodo)) return nodo.map(textoDe).join('');
    if (React.isValidElement(nodo)) return textoDe((nodo.props as { children?: React.ReactNode }).children);
    return '';
};

const esCeldaNumerica = (children: React.ReactNode): boolean => {
    const texto = textoDe(children).trim();
    if (texto.length === 0) return false;

    const numeroMatch = NUMERO_PREFIJO.exec(texto);
    if (!numeroMatch || numeroMatch[0].length === 0) return false;

    const resto = texto.slice(numeroMatch[0].length).trim();
    return resto === '' || UNIDADES_CORTAS.has(resto.toLowerCase());
};

// Componentes de markdown como funciones nombradas a nivel de módulo — no se recrean en
// cada render (antes vivían como funciones flecha inline dentro de un useMemo, una por
// cada tag, patrón que Sonar marcaba 26 veces como "Move this component definition out
// of the parent component"). Ninguna depende de props o estado de Response, así que no
// hace falta useMemo: el objeto de abajo es una constante estática.
//
// Cada clase está verificada en estilos computados contra markdown.css: solo quedan las
// que efectivamente ganan la cascada. Las que perdían se borraron en QS-19224 · C-4 —
// eran ~13 clases que el CSS sobreescribía y que hacían parecer que el estilo vivía en
// dos lugares.
//
// Si agregás una clase acá, verificá que se aplique: markdown.css usa selectores de dos
// niveles (.markdown-body p, .markdown-body--enhanced table td) que le ganan por
// especificidad a cualquier utilidad de una sola clase.

// Texto — whitespace-pre-wrap sí gana: preserva los saltos del markdown
function MarkdownParagraph({ children, ...props }: React.ComponentProps<'p'>) {
    return (
        <p {...props} className="whitespace-pre-wrap">
            {children}
        </p>
    );
}
function MarkdownH1({ children, ...props }: React.ComponentProps<'h1'>) {
    return <h1 {...props}>{children}</h1>;
}
function MarkdownH2({ children, ...props }: React.ComponentProps<'h2'>) {
    return <h2 {...props}>{children}</h2>;
}
function MarkdownH3({ children, ...props }: React.ComponentProps<'h3'>) {
    return <h3 {...props}>{children}</h3>;
}
function MarkdownH4({ children, ...props }: React.ComponentProps<'h4'>) {
    return <h4 {...props}>{children}</h4>;
}
function MarkdownH5({ children, ...props }: React.ComponentProps<'h5'>) {
    return <h5 {...props}>{children}</h5>;
}
function MarkdownH6({ children, ...props }: React.ComponentProps<'h6'>) {
    return <h6 {...props}>{children}</h6>;
}

// Énfasis
function MarkdownStrong({ children, ...props }: React.ComponentProps<'strong'>) {
    return <strong {...props}>{children}</strong>;
}
function MarkdownEm({ children, ...props }: React.ComponentProps<'em'>) {
    return <em {...props}>{children}</em>;
}
function MarkdownB({ children, ...props }: React.ComponentProps<'b'>) {
    return <b {...props}>{children}</b>;
}
function MarkdownI({ children, ...props }: React.ComponentProps<'i'>) {
    return <i {...props}>{children}</i>;
}

// Listas — sin ml-6: se sumaba al padding-left: 2em del CSS y daba doble sangría
function MarkdownUl({ children, ...props }: React.ComponentProps<'ul'>) {
    return (
        <ul {...props} className="list-disc">
            {children}
        </ul>
    );
}
function MarkdownOl({ children, ...props }: React.ComponentProps<'ol'>) {
    return (
        <ol {...props} className="list-decimal">
            {children}
        </ol>
    );
}
// B-8 · sin `my-1`: el espaciado entre bullets vive solo en markdown.css
// (`.markdown-body li + li`), para no pelear por el mismo margen en dos lugares.
function MarkdownLi({ children, ...props }: React.ComponentProps<'li'>) {
    return <li {...props}>{children}</li>;
}

// Tablas — el overflow del contenedor vive en markdown.css
function MarkdownTable({ children, ...props }: React.ComponentProps<'table'>) {
    return (
        <div className="table-container">
            <table {...props} className="min-w-full border-collapse">
                {children}
            </table>
        </div>
    );
}
function MarkdownThead({ children, ...props }: React.ComponentProps<'thead'>) {
    return <thead {...props}>{children}</thead>;
}
function MarkdownTbody({ children, ...props }: React.ComponentProps<'tbody'>) {
    return <tbody {...props}>{children}</tbody>;
}
function MarkdownTr({ children, ...props }: React.ComponentProps<'tr'>) {
    return <tr {...props}>{children}</tr>;
}
function MarkdownTh({ children, ...props }: React.ComponentProps<'th'>) {
    return (
        <th {...props} className="markdown-table-header">
            {children}
        </th>
    );
}
// Sin style inline: el overflow: visible que había acá anulaba el del CSS y dejaba las
// celdas sin envolver y sin cortar. El wrap se define en markdown.css. La clase --num la
// agrega B-4 para que los números usen cifras de ancho fijo.
function MarkdownTd({ children, ...props }: React.ComponentProps<'td'>) {
    return (
        <td
            {...props}
            className={
                esCeldaNumerica(children) ? 'markdown-table-cell markdown-table-cell--num' : 'markdown-table-cell'
            }
        >
            {children}
        </td>
    );
}

// Código — fondo, padding, radio y tipografía monoespaciada vienen del CSS
function MarkdownPre({ children, ...props }: React.ComponentProps<'pre'>) {
    return <pre {...props}>{children}</pre>;
}
function MarkdownCode({ children, inline, ...props }: React.ComponentProps<'code'> & { inline?: boolean }) {
    if (inline) {
        return <code {...props}>{children}</code>;
    }
    return (
        <code {...props} className="block">
            {children}
        </code>
    );
}

// Links — color y subrayado salen de los tokens del tema
function MarkdownA({ children, ...props }: React.ComponentProps<'a'>) {
    return (
        <a {...props} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
}

// Otros — italic sí gana; el borde y los márgenes vienen del CSS
function MarkdownBlockquote({ children, ...props }: React.ComponentProps<'blockquote'>) {
    return (
        <blockquote {...props} className="italic">
            {children}
        </blockquote>
    );
}
function MarkdownHr({ ...props }: React.ComponentProps<'hr'>) {
    return <hr {...props} />;
}
function MarkdownImg({ alt = '', ...props }: React.ComponentProps<'img'>) {
    return <img {...props} alt={alt} className="h-auto rounded-md" />;
}

const markdownComponents = {
    p: MarkdownParagraph,
    h1: MarkdownH1,
    h2: MarkdownH2,
    h3: MarkdownH3,
    h4: MarkdownH4,
    h5: MarkdownH5,
    h6: MarkdownH6,
    strong: MarkdownStrong,
    em: MarkdownEm,
    b: MarkdownB,
    i: MarkdownI,
    ul: MarkdownUl,
    ol: MarkdownOl,
    li: MarkdownLi,
    table: MarkdownTable,
    thead: MarkdownThead,
    tbody: MarkdownTbody,
    tr: MarkdownTr,
    th: MarkdownTh,
    td: MarkdownTd,
    pre: MarkdownPre,
    code: MarkdownCode,
    a: MarkdownA,
    blockquote: MarkdownBlockquote,
    hr: MarkdownHr,
    img: MarkdownImg,
};

export const Response = ({
    id,
    interactionId, // ID real del backend para rating
    text,
    animated = true,
    attachment,
    mailToken = null,
    mail = null,
    payload,
    ratingMsg,
    messageType,
    isLoading,
}: {
    id: string;
    interactionId?: string; // Solo disponible cuando la interacción está guardada en el backend
    text: string;
    ratingMsg?: string | null;
    animated?: boolean;
    attachment?: Attachment[];
    mailToken?: string | null;
    mail?: string | null;
    payload: FeedbackConversation;
    messageType?: ConversationType['messageType'];
    isLoading?: boolean;
}) => {
    const [rating, setRating] = useState<RatingValue | null>(() => {
        if (ratingMsg === 'positive') return 'up';
        if (ratingMsg === 'negative') return 'down';
        return null;
    });
    const [showRatingInput, setShowRatingInput] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const { t } = useTranslation();
    const { mutateAsync, isPending } = useRateConversation();
    const { conversationId } = useChatContext();
    const { toast } = useToast();
    const theme = useTheme();

    const handleToggle = useCallback(
        (value: RatingValue) => {
            if (!conversationId) {
                console.error('No conversation ID available for rating');
                return;
            }
            // Solo permitir rating si hay un interactionId real del backend
            if (!interactionId) {
                console.warn(
                    'Cannot rate response: No real interaction ID available yet. Please wait for the response to complete.',
                );
                toast({
                    title: t('errors.ratingNotAvailableTitle'),
                    description: t('errors.ratingNotAvailableDescription'),
                    variant: 'destructive',
                });
                return;
            }

            if (value === rating) {
                setRating(null);
                mutateAsync({
                    conversationId,
                    interactionId: interactionId,
                    rating: null as unknown as 'positive' | 'negative', // API accepts null for rating removal
                    feedbackComment: '',
                    agent_version: payload.agent_version,
                }).catch(() => {
                    setRating(value);
                    toast({
                        title: t('errors.ratingNotSavedTitle'),
                        description: t('errors.ratingNotSavedDescription'),
                        variant: 'destructive',
                    });
                });
            } else {
                setRating(value);
                mutateAsync({
                    conversationId,
                    interactionId: interactionId, // Usar el ID real del backend
                    rating: RATE_MAP[value],
                    feedbackComment: '',
                    agent_version: payload.agent_version,
                }).catch(() => {
                    setRating(null); // Reset on error
                    toast({
                        title: t('errors.ratingNotSavedTitle'),
                        description: t('errors.ratingNotSavedDescription'),
                        variant: 'destructive',
                    });
                });
            }

            setShowRatingInput(value === 'down');
        },
        [rating, conversationId, interactionId, mutateAsync, toast, t, payload.agent_version],
    );

    const processedText = useMemo(() => normalizeMarkdownText(text || ''), [text]);

    const handleResetRating = useCallback((justInputState?: boolean) => {
        if (!justInputState) setRating(null);
        setShowRatingInput(false);
    }, []);

    const handleUpdateStatus = useCallback((newStatus: 'idle' | 'success' | 'error') => {
        setStatus(newStatus);
    }, []);

    // Early return if no text and not thinking/loading
    if (!text?.trim() && messageType !== 'thinking' && !isLoading) {
        return null;
    }

    // Si es tipo "thinking", mostrar loader personalizado
    if (messageType === 'thinking') {
        return (
            <motion.div
                className="px-2 self-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
            >
                <div className="flex items-center gap-3 py-2">
                    <ThinkingLoader />
                    <span className="animate-pulse italic">
                        {text.endsWith('...') ? (
                            <>
                                {text.slice(0, -3)}
                                <AnimatedDots />
                            </>
                        ) : (
                            text
                        )}
                    </span>
                </div>
            </motion.div>
        );
    }

    /*
     * Una sola animación de entrada, y gobernada por `animated`. Antes eran dos
     * motion.div anidados —uno con y: 20 y otro con x: -20, los dos a 500 ms— y el de
     * afuera corría siempre, así que los mensajes se re-animaban al abrir una
     * conversación del historial. Ver QS-19224 · C-3.
     */
    return (
        <motion.div
            className="px-2 self-start"
            initial={animated ? { opacity: 0, y: 6 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
        >
            <div className="group flex flex-col space-y-2 items-start">
                <div
                    className="inline-block markdown-body markdown-body--enhanced"
                    data-theme={theme.theme}
                    data-testid="agent-response"
                >
                    {/*
                     * El tipeado progresivo se queda. Sacarlo fue una propuesta del análisis
                     * técnico de QS-19224 (C-2), no un pedido de producto; se probó en local y
                     * se prefirió conservar el efecto visual de que la respuesta se escribe.
                     *
                     * Lo que cuesta, para que quede dicho: la velocidad es fija (5 caracteres
                     * cada 20 ms = 250 car/s) y el backend ya entrega los tokens de forma
                     * progresiva, así que el tipeado se atrasa respecto del stream y agrega
                     * entre +8% y +40% al tiempo total según el tamaño de la respuesta.
                     *
                     * Si alguna vez se quiere el efecto sin ese costo, el camino es hacer la
                     * velocidad adaptativa —que se acelere según lo que falta por mostrar, de
                     * modo que termine junto con el stream— y no volver a sacar el tipeado.
                     */}
                    <div className="markdown-wrapper">
                        {payload.agent_version === 'v3' && messageType ? (
                            <Typewriter
                                text={processedText}
                                speed={20}
                                chunkSize={5}
                                className="markdown-body"
                                markdownComponents={markdownComponents}
                                id={id}
                            />
                        ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} key={id}>
                                {processedText}
                            </ReactMarkdown>
                        )}

                        {attachment?.map((item, index) => (
                            <ResponseAttachment key={index} {...item} />
                        ))}
                    </div>
                </div>
                <MessageActions
                    rating={rating}
                    isPending={isPending}
                    showFeedbackInput={showRatingInput}
                    status={status}
                    textToCopy={text || ''}
                    onRatingToggle={handleToggle}
                    canRate={!!interactionId}
                />
                {rating === 'down' && showRatingInput && mail && (
                    <FeedbackBox
                        interactionId={interactionId || id} // Usar interactionId real si está disponible
                        token={mailToken || localStorage.getItem('token') || ''}
                        resetRating={handleResetRating}
                        status={status}
                        updateStatus={handleUpdateStatus}
                        payload={payload}
                    />
                )}
            </div>
        </motion.div>
    );
};

export default React.memo(Response);
